import { SupabaseClient } from '@supabase/supabase-js';
import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface UploadFormProps {
  supabase: SupabaseClient;
  bucketName: string;
  environment: string;
  onUploadComplete: () => void;
}

const UploadForm: React.FC<UploadFormProps> = ({ 
  supabase,
  bucketName,
  environment,
  onUploadComplete
}) => {
  const [files, setFiles] = useState<FileList | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const allowedFileTypes = [
    'image/jpeg', 
    'image/png', 
    'image/gif', 
    'image/webp',
    'video/mp4', 
    'video/quicktime', 
    'video/x-msvideo'
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    
    if (!selectedFiles || selectedFiles.length === 0) {
      return;
    }
    
    // Check if all selected files are valid
    const invalidFiles = Array.from(selectedFiles).filter(
      file => !allowedFileTypes.includes(file.type)
    );
    
    if (invalidFiles.length > 0) {
      setError(`Invalid file type(s): ${invalidFiles.map(f => f.name).join(', ')}`);
      setFiles(null);
      return;
    }
    
    setFiles(selectedFiles);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!files || files.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    
    try {
      const totalFiles = files.length;
      let completedFiles = 0;
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileExt = file.name.split('.').pop();
        const fileName = `${uuidv4()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(environment + '/' + fileName, file);
          
        if (uploadError) {
          throw new Error(`Error uploading ${file.name}: ${uploadError.message}`);
        }
        
        completedFiles++;
        setUploadProgress(Math.round((completedFiles / totalFiles) * 100));
      }
      
      // Reset form
      setFiles(null);
      setUploadProgress(0);
      
      // Notify parent component to refresh the gallery
      onUploadComplete();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Upload Media Files</h2>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <label 
            htmlFor="file-upload" 
            className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition duration-150 ease-in-out"
          >
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-gray-500 mb-2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
              </svg>
              <p className="mb-2 text-sm text-gray-500">
                <span className="font-semibold">Click to upload</span>
                <span className="hidden md:block"> or drag and drop</span>
              </p>
              <p className="text-xs text-gray-500">
                {files && files.length > 0 
                  ? <span className="text-blue-500">{files.length} file(s) selected</span>
                  : ''}
              </p>
            </div>
            <input
              id="file-upload"
              type="file"
              className="hidden"
              onChange={handleFileChange}
              accept=".jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi"
              multiple
              disabled={uploading}
            />
          </label>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        
        {uploading && (
          <div className="mb-6">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-primary h-2.5 rounded-full transition-all duration-300" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <div className="text-center text-sm text-gray-600 mt-2">{uploadProgress}%</div>
          </div>
        )}
        
        <div className="flex justify-end space-x-4">
          <button 
            type="button" 
            className="py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-md transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
            onClick={onUploadComplete}
            disabled={uploading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className={`py-2 px-4 font-medium rounded-md transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary
              ${(!files || uploading) 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-primary hover:bg-primary-dark text-white'}`}
            disabled={!files || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Files'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UploadForm;